import clsx from "clsx";
import React, { useMemo, useState } from "react";
import {
  highlightMatchesPlan,
  isBundlePlan,
  isLivePlan,
  isManualSubscription,
  priceUnitLabel,
  selectionHasBundle,
  sortMainPlans,
  splitCatalogPlans,
} from "../../Data/billingPlans";
import { CcButton, CcConfirmModal } from "../../design-system/carecentre";
import { PlanCollapsedCopy } from "./WysiwygHtml";
import { useStyles } from "./style";

const trialDaysFrom = (days) => {
  const value = Number(days);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

const trialDaysLabel = (days) => {
  const trialDays = trialDaysFrom(days);
  return trialDays > 0 ? `${trialDays}-day trial` : "No trial";
};

const statusLabel = (status) =>
  ({
    trialing: "Trial",
    active: "Active",
    past_due: "Past due",
  })[status] || status;

const formatPlanDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
};

const livePlanDate = (plan) => {
  const ending = Boolean(plan?.cancel_at_period_end);
  const date = formatPlanDate(plan?.trial_ends_at || plan?.current_period_end);
  if (plan?.status === "past_due") return date ? `Due ${date}` : null;
  if (!date) return null;
  if (ending) return `Ends ${date}`;
  if (plan.status === "trialing") return `Trial ends ${date}`;
  return `Renews ${date}`;
};

const joinNames = (names = []) => {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] || "your current plans";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
};

const subscriptionForPlan = (plan, subscriptions = []) =>
  (subscriptions || []).find((row) => row?.plan_code === plan?.code) || null;

const hasStripeSubscription = (subscription) =>
  String(subscription?.stripe_subscription_id || "").trim() !== "";

const canPurchase = (plan) =>
  Boolean(plan?.selectable) && !isLivePlan(plan) && !plan?.already_granted;

const PlanBadge = ({ plan, highlighted, included }) => {
  const classes = useStyles();
  const live = isLivePlan(plan);
  const featured = isBundlePlan(plan);
  const ending = Boolean(plan?.cancel_at_period_end);
  const isIncluded = included ?? Boolean(plan.included_in_bundle);

  if (live) {
    const tone =
      ending || plan.status === "past_due"
        ? "warn"
        : plan.status === "trialing"
          ? "trial"
          : "ok";
    return (
      <span
        className={clsx(classes.badge, {
          [classes.badgeLive]: tone === "ok",
          [classes.badgeTrial]: tone === "trial",
          [classes.badgeWarn]: tone === "warn",
        })}
      >
        {ending ? "Ending" : statusLabel(plan.status)}
      </span>
    );
  }
  if (plan.already_granted) {
    return (
      <span className={clsx(classes.badge, classes.badgeLive)}>Selected</span>
    );
  }
  if (isIncluded) {
    return (
      <span className={clsx(classes.badge, classes.badgeBundle)}>Included</span>
    );
  }
  if (featured) {
    return (
      <span className={clsx(classes.badge, classes.badgeFeatured)}>
        All modules
      </span>
    );
  }
  if (highlighted && plan.selectable) {
    return (
      <span className={clsx(classes.badge, classes.badgeMatch)}>
        Includes this
      </span>
    );
  }
  return (
    <span className={clsx(classes.badge, classes.badgeTrial)}>
      {plan.trial_available === false
        ? "No trial"
        : trialDaysLabel(plan.trial_days)}
    </span>
  );
};

const MainPlanCard = ({
  plan,
  highlightItem,
  subscription,
  hasLiveModules,
  selected = false,
  selectionMode = false,
  onBuy,
  onAdjust,
  onCancel,
  onKeep,
  onBookDemo,
  busy,
  coveredByBundle = false,
}) => {
  const classes = useStyles();
  const live = isLivePlan(plan);
  const alreadyGranted = Boolean(plan.already_granted);
  const included = Boolean(plan.included_in_bundle) || coveredByBundle;
  const featured = isBundlePlan(plan);
  const ending = Boolean(plan.cancel_at_period_end);
  const canUpgradeToBundle =
    featured && !live && !alreadyGranted && !included && hasLiveModules;
  const canBuy = plan.selectable || canUpgradeToBundle;
  const locked = alreadyGranted || included || live || !canBuy;
  const highlighted = highlightMatchesPlan(plan, highlightItem);
  const due = live ? livePlanDate(plan) : null;
  const manageLive =
    live &&
    hasStripeSubscription(subscription) &&
    Boolean(onAdjust || onCancel);
  const buyLabel = live
    ? "Current"
    : alreadyGranted
      ? "Selected"
      : included
        ? "Included"
        : canUpgradeToBundle
          ? "Upgrade"
          : selected
            ? "Added"
            : plan.selectable
              ? selectionMode
                ? "Add"
                : "Buy"
              : "Unavailable";

  return (
    <article
      className={clsx(classes.planCard, {
        [classes.planCardFeatured]: featured,
        [classes.planCardLive]: live || alreadyGranted,
        [classes.planCardIncluded]: included,
        [classes.planCardHighlight]:
          (highlighted && plan.selectable && !featured) || selected,
        [classes.planCardDisabled]: locked && !live && !alreadyGranted,
      })}
    >
      <PlanBadge plan={plan} highlighted={highlighted} included={included} />
      <div className={classes.planName}>{plan.name}</div>
      <div className={classes.price}>
        £{plan.price}
        <span className={classes.priceSmall}>{priceUnitLabel(plan)}</span>
      </div>
      {due ? <p className={classes.planLiveMeta}>{due}</p> : null}
      <PlanCollapsedCopy
        plan={plan}
        excerptClassName={classes.planDesc}
        htmlClassName={classes.planHtml}
        listClassName={classes.featureList}
        moreClassName={classes.viewMore}
        previewClassName={classes.planPreview}
      />
      {plan.blocked_reason && !plan.selectable && !live ? (
        <p className={classes.blockedReason}>{plan.blocked_reason}</p>
      ) : null}
      <div className={classes.cardFooter}>
        <div className={classes.cardActions}>
          {manageLive ? (
            <>
              {onAdjust ? (
                <CcButton
                  variant="secondary"
                  size="sm"
                  className={classes.fullBtn}
                  disabled={busy}
                  onClick={() => onAdjust(plan)}
                >
                  Adjust
                </CcButton>
              ) : null}
              {ending &&
              hasStripeSubscription(subscription) &&
              subscription?.id &&
              onKeep ? (
                <CcButton
                  variant="secondary"
                  size="sm"
                  className={classes.fullBtn}
                  disabled={busy}
                  onClick={() => onKeep(plan)}
                >
                  Keep
                </CcButton>
              ) : subscription?.id && onCancel ? (
                <CcButton
                  variant="ghost"
                  size="sm"
                  className={clsx(classes.fullBtn, classes.planCancel)}
                  disabled={busy}
                  onClick={() => onCancel(plan)}
                >
                  Cancel
                </CcButton>
              ) : null}
            </>
          ) : (
            <>
              <CcButton
                variant={selected ? "secondary" : "primary"}
                size="sm"
                className={classes.fullBtn}
                disabled={busy || locked}
                onClick={() => onBuy?.(plan)}
              >
                {buyLabel}
              </CcButton>
              {onBookDemo ? (
                <CcButton
                  variant="secondary"
                  size="sm"
                  className={classes.fullBtn}
                  disabled={busy}
                  onClick={() => onBookDemo(plan)}
                >
                  Book a demo
                </CcButton>
              ) : null}
            </>
          )}
        </div>
      </div>
    </article>
  );
};

export const getStandaloneIcon = (codeOrName = "") => {
  const key = String(codeOrName).toLowerCase();
  if (key.includes("chat")) {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    );
  }
  if (key.includes("feed")) {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 11a9 9 0 0 1 9 9" />
        <path d="M4 4a16 16 0 0 1 16 16" />
        <circle cx="5" cy="19" r="1" />
      </svg>
    );
  }
  if (key.includes("maintenance")) {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    );
  }
  if (key.includes("survey")) {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    );
  }
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
};

const StandaloneCard = ({
  plan,
  highlightItem,
  subscription,
  selected = false,
  selectionMode = false,
  onBuy,
  onCancel,
  onKeep,
  busy,
  coveredByBundle = false,
}) => {
  const classes = useStyles();
  const manual = isManualSubscription(plan);
  const live = isLivePlan(plan) || plan.already_granted;
  const highlighted = highlightMatchesPlan(plan, highlightItem);
  const included = Boolean(plan.included_in_bundle) || coveredByBundle;
  const canBuy = canPurchase(plan) && !included;
  const ending = Boolean(plan.cancel_at_period_end);
  const due = isLivePlan(plan) ? livePlanDate(plan) : null;

  return (
    <article
      className={clsx(classes.addonCard, {
        [classes.addonCardLive]: live,
        [classes.addonCardHighlight]: (highlighted || selected) && !included,
        [classes.planCardIncluded]: included && !live,
      })}
    >
      <div className={classes.addonHead}>
        <div className={classes.addonIconBox}>
          {getStandaloneIcon(plan.code || plan.name)}
        </div>
        {live ? (
          <span
            className={clsx(classes.badge, classes.addonBadge, {
              [classes.badgeLive]:
                !ending &&
                plan.status !== "trialing" &&
                plan.status !== "past_due",
              [classes.badgeTrial]: !ending && plan.status === "trialing",
              [classes.badgeWarn]: ending || plan.status === "past_due",
            })}
          >
            {manual
              ? "Manual"
              : plan.already_granted
                ? "Selected"
                : ending
                  ? "Ending"
                  : statusLabel(plan.status) || "Active"}
          </span>
        ) : included ? (
          <span
            className={clsx(
              classes.badge,
              classes.addonBadge,
              classes.badgeBundle,
            )}
          >
            Included
          </span>
        ) : null}
      </div>
      <div className={classes.addonBody}>
        <span className={classes.addonName}>{plan.name}</span>
        <div className={classes.addonPrice}>
          £{plan.price}
          <small>{priceUnitLabel(plan)}</small>
        </div>
        {due ? <p className={classes.planLiveMeta}>{due}</p> : null}
        <PlanCollapsedCopy
          plan={plan}
          excerptClassName={classes.addonDesc}
          htmlClassName={classes.planHtml}
          listClassName={classes.featureList}
          moreClassName={classes.viewMore}
        />
      </div>
      <div className={classes.addonFooter}>
        {isLivePlan(plan) &&
        ending &&
        hasStripeSubscription(subscription) &&
        subscription?.id &&
        onKeep ? (
          <CcButton
            variant="secondary"
            size="sm"
            className={classes.addonBuy}
            disabled={busy}
            onClick={() => onKeep(plan)}
          >
            Keep
          </CcButton>
        ) : isLivePlan(plan) &&
          hasStripeSubscription(subscription) &&
          subscription?.id &&
          onCancel ? (
          <CcButton
            variant="ghost"
            size="sm"
            className={clsx(classes.addonBuy, classes.planCancel)}
            disabled={busy}
            onClick={() => onCancel(plan)}
          >
            Cancel
          </CcButton>
        ) : live ? (
          <div className={classes.addonActiveStatus}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>{manual ? "Manual subscription" : "Active on plan"}</span>
          </div>
        ) : included ? (
          <div className={classes.addonActiveStatus}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Included in plan</span>
          </div>
        ) : canBuy ? (
          <CcButton
            variant={selected ? "primary" : "secondary"}
            size="sm"
            className={classes.addonBuy}
            disabled={busy}
            onClick={() => onBuy?.(plan)}
          >
            {selected ? "Added" : selectionMode ? "Add" : "Buy"}
          </CcButton>
        ) : (
          <span className={classes.addonUnavailable}>Unavailable</span>
        )}
      </div>
    </article>
  );
};

const PlanCards = ({
  plans = [],
  subscriptions = [],
  highlightItem,
  selectedCodes,
  onBuy,
  onAdjust,
  onCancel,
  onKeep,
  onBookDemo,
  busy = false,
  loading = false,
}) => {
  const classes = useStyles();
  const [upgradePlan, setUpgradePlan] = useState(null);
  const { main, standalone } = useMemo(() => splitCatalogPlans(plans), [plans]);
  const mainPlans = useMemo(() => sortMainPlans(main), [main]);
  const selectionMode = Array.isArray(selectedCodes);
  const bundleSelected =
    selectionMode && selectionHasBundle(selectedCodes, plans);
  const hasLiveModules = useMemo(
    () => (plans || []).some(isLivePlan) || (subscriptions || []).length > 0,
    [plans, subscriptions],
  );
  const liveNames = useMemo(
    () =>
      (plans || [])
        .filter((plan) => isLivePlan(plan) && !isBundlePlan(plan))
        .map((plan) => plan.name)
        .filter(Boolean),
    [plans],
  );

  const requestBuy = (plan) => {
    if (selectionMode && bundleSelected && !selectedCodes.includes(plan.code)) {
      return;
    }
    if (!selectionMode) {
      const upgrading =
        isBundlePlan(plan) &&
        !isLivePlan(plan) &&
        !plan?.already_granted &&
        !plan?.included_in_bundle &&
        hasLiveModules;
      if (upgrading) {
        setUpgradePlan(plan);
        return;
      }
    }
    onBuy?.(plan);
  };

  if (loading && !plans.length) {
    return <p className={classes.catalogStatus}>Loading plans…</p>;
  }

  if (!plans.length) {
    return <p className={classes.catalogStatus}>No plans are available yet.</p>;
  }

  return (
    <div className={classes.catalog}>
      {mainPlans.length > 0 ? (
        <div className={classes.planGrid}>
          {mainPlans.map((plan) => (
            <MainPlanCard
              key={plan.id || plan.code}
              plan={plan}
              highlightItem={highlightItem}
              subscription={subscriptionForPlan(plan, subscriptions)}
              hasLiveModules={hasLiveModules}
              selected={selectionMode && selectedCodes.includes(plan.code)}
              selectionMode={selectionMode}
              coveredByBundle={
                bundleSelected && !selectedCodes.includes(plan.code)
              }
              onBuy={requestBuy}
              onAdjust={onAdjust}
              onCancel={onCancel}
              onKeep={onKeep}
              onBookDemo={onBookDemo}
              busy={busy}
            />
          ))}
        </div>
      ) : null}

      {standalone.length > 0 ? (
        <section className={classes.standaloneSection}>
          <p className={classes.standaloneLabel}>Standalone products</p>
          <div className={classes.addonGrid}>
            {standalone.map((plan) => (
              <StandaloneCard
                key={plan.id || plan.code}
                plan={plan}
                highlightItem={highlightItem}
                subscription={subscriptionForPlan(plan, subscriptions)}
                selected={selectionMode && selectedCodes.includes(plan.code)}
                selectionMode={selectionMode}
                coveredByBundle={
                  bundleSelected && !selectedCodes.includes(plan.code)
                }
                onBuy={requestBuy}
                onCancel={onCancel}
                onKeep={onKeep}
                busy={busy}
              />
            ))}
          </div>
        </section>
      ) : null}

      <CcConfirmModal
        open={Boolean(upgradePlan)}
        onClose={() => setUpgradePlan(null)}
        onConfirm={() => {
          const plan = upgradePlan;
          setUpgradePlan(null);
          if (plan) onBuy?.(plan);
        }}
        title={upgradePlan ? `Upgrade to ${upgradePlan.name}?` : "Upgrade"}
        description={
          upgradePlan
            ? `${joinNames(liveNames)} will be replaced by ${
                upgradePlan.name
              }. You’ll only pay the remaining-period difference.`
            : ""
        }
        confirmLabel="Upgrade"
        cancelLabel="Not now"
        disabled={busy}
      />
    </div>
  );
};

export default PlanCards;
